import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { moodAPI } from '../../../services';
import { Smile, BarChart3, Users, AlertCircle, RefreshCw, Settings, X } from 'lucide-react';

const MOOD_EMOJI = { 1:'😢', 2:'😕', 3:'😐', 4:'😊', 5:'😄' };
const MOOD_LABEL = { 1:'Very Sad', 2:'Sad', 3:'Neutral', 4:'Happy', 5:'Very Happy' };
const MOOD_COLOR = { 1:'text-red-600', 2:'text-orange-500', 3:'text-yellow-500', 4:'text-green-500', 5:'text-emerald-600' };

const MoodDashboardPage = () => {
  const { user } = useAuth();
  // Editing the policy stays admin-only server-side (PUT /mood/policy) - hide the entry
  // point for viewers who only have the read-only canViewMoodAnalytics permission
  const canEditPolicy = user?.role === 'admin' || user?.role === 'superadmin';
  const [data, setData] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPolicy, setShowPolicy] = useState(false);
  const [policyForm, setPolicyForm] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [dash, pol] = await Promise.all([moodAPI.getDashboard(), moodAPI.getPolicy()]);
      setData(dash?.data || null);
      setPolicy(pol?.data || null);
      setPolicyForm(pol?.data || { allowSkip:true, maxSkipsPerMonth:3, managerAlertsEnabled:true, alertThreshold:2.5 });
    } catch { showToast('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePolicy = async () => {
    try { await moodAPI.updatePolicy(policyForm); showToast('Policy saved'); setShowPolicy(false); load(); }
    catch { showToast('Save failed', 'error'); }
  };

  const avgColor = (score) => {
    const n = parseFloat(score);
    if (n >= 4) return 'text-green-600';
    if (n >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type==='error'?'bg-red-500':'bg-green-500'}`}>{toast.msg}</div>}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Smile className="w-6 h-6 text-blue-600"/>Mood Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">Monitor employee well-being and sentiment trends</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"><RefreshCw className="w-4 h-4"/></button>
          {canEditPolicy && (
            <button onClick={() => setShowPolicy(true)} className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"><Settings className="w-4 h-4"/>Policy</button>
          )}
        </div>
      </div>

      {loading ? <div className="text-center py-20 text-gray-400">Loading...</div> : !data ? null : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label:'Avg Mood Score', value: data.avgScore, icon:Smile, extra: data.avgScore ? MOOD_EMOJI[Math.round(parseFloat(data.avgScore))] : '—' },
              { label:'Total Responses', value: data.total, icon:Users },
              { label:'Total Skips', value: data.totalSkips, icon:AlertCircle },
              { label:'Departments', value: data.departments?.length || 0, icon:BarChart3 }
            ].map(({ label, value, icon:Icon, extra }) => (
              <div key={label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
                <Icon className="w-8 h-8 text-blue-500"/>
                <div>
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className={`text-xl font-bold ${label==='Avg Mood Score'?avgColor(value):'text-gray-900'}`}>{extra || value}</p>
                  {extra && <p className="text-sm font-bold text-gray-700">{value}</p>}
                </div>
              </div>
            ))}
          </div>

          {/* Department breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-100 font-semibold text-gray-800 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-500"/>Department Mood</div>
              {data.departments?.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No data</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>{['Department','Avg Mood','Responses'].map(h=><th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-500">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.departments?.map(d => (
                      <tr key={d.name} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 font-medium text-gray-900">{d.name}</td>
                        <td className="px-4 py-2.5">
                          <span className={`font-bold text-lg ${avgColor(d.avgScore)}`}>{MOOD_EMOJI[Math.round(parseFloat(d.avgScore))]} {d.avgScore}</span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">{d.responses}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Recent Notes */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-100 font-semibold text-gray-800">Recent Comments</div>
              {data.recentNotes?.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No comments</p>
              ) : (
                <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
                  {data.recentNotes?.map((n, i) => (
                    <div key={i} className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm text-gray-900">{n.employee}</span>
                        <span className={`text-lg ${MOOD_COLOR[n.score]}`}>{MOOD_EMOJI[n.score]}</span>
                      </div>
                      <p className="text-sm text-gray-600 italic">"{n.note}"</p>
                      <p className="text-xs text-gray-400 mt-0.5">{new Date(n.date).toLocaleDateString('en-IN')}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Policy Modal */}
      {showPolicy && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-semibold">Mood Policy Settings</h2>
              <button onClick={() => setShowPolicy(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-4">
              <label className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Allow employees to skip</span>
                <input type="checkbox" checked={policyForm.allowSkip||false} onChange={e=>setPolicyForm(f=>({...f,allowSkip:e.target.checked}))} className="w-5 h-5 text-blue-600"/>
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max skips per month</label>
                <input type="number" min="0" max="31" value={policyForm.maxSkipsPerMonth||3} onChange={e=>setPolicyForm(f=>({...f,maxSkipsPerMonth:parseInt(e.target.value)}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/>
              </div>
              <label className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Manager alerts enabled</span>
                <input type="checkbox" checked={policyForm.managerAlertsEnabled||false} onChange={e=>setPolicyForm(f=>({...f,managerAlertsEnabled:e.target.checked}))} className="w-5 h-5 text-blue-600"/>
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alert threshold (avg score below)</label>
                <input type="number" min="1" max="5" step="0.1" value={policyForm.alertThreshold||2.5} onChange={e=>setPolicyForm(f=>({...f,alertThreshold:parseFloat(e.target.value)}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/>
                <p className="text-xs text-gray-400 mt-1">Alert triggers when team average drops below this value (1–5)</p>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowPolicy(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={savePolicy} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">Save Policy</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoodDashboardPage;
